import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';

const ExpenseDashboard = () => {
    const [activeTab, setActiveTab] = useState('promotions');
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ promo: 0, karigar: 0, embroidery: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

    useEffect(() => {
        fetchData();
        fetchSummary();
    }, [activeTab]);

    const fetchSummary = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/expenses-summary`);
            const data = await res.json();
            setSummary(data);
        } catch (err) { console.error("Summary error", err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'promotions') {
                const res = await fetch(`${API_BASE_URL}/articleshowa`);
                const articles = await res.json();
                
                const allPromos = [];
                articles.forEach(article => {
                    const promoList = article.promotions || article.promos;
                    if (promoList && Array.isArray(promoList)) {
                        promoList.forEach(p => {
                            allPromos.push({
                                _id: p._id || Math.random().toString(),
                                date: p.date,
                                title: article.name, 
                                source: p.from,
                                amount: p.cost || 0,
                                payeeName: article.name // for unified sorting key
                            });
                        });
                    }
                });
                setExpenses(sortData(allPromos, sortConfig.key, sortConfig.direction));
            } else {
                const res = await fetch(`${API_BASE_URL}/expenses/${activeTab}`);
                const data = await res.json();
                const formattedData = data.map(d => ({...d, title: d.payeeName, source: d.description || 'N/A'}));
                setExpenses(sortData(formattedData, sortConfig.key, sortConfig.direction));
            }
        } catch (err) {
            console.error("Fetch error", err);
        } finally {
            setLoading(false);
        }
    };

    const sortData = (data, key, direction) => {
        return [...data].sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            if (key === 'date') {
                valA = new Date(valA || 0);
                valB = new Date(valB || 0);
            }

            if (valA < valB) return direction === 'asc' ? -1 : 1;
            if (valA > valB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setExpenses(sortData(expenses, key, direction));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1 opacity-50 small"></i>;
        return sortConfig.direction === 'asc' 
            ? <i className="bi bi-sort-up ms-1 text-primary small"></i> 
            : <i className="bi bi-sort-down ms-1 text-primary small"></i>;
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="container">
                <h2 className="fw-bold mb-4">Expense <span className="text-primary">Management</span></h2>

                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm bg-primary text-white">
                            <div className="card-body">
                                <small className="text-uppercase opacity-75">Promotions Total</small>
                                <h3 className="mb-0">₹{summary.promo.toLocaleString()}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm bg-success text-white">
                            <div className="card-body">
                                <small className="text-uppercase opacity-75">Karigar Total</small>
                                <h3 className="mb-0">₹{summary.karigar.toLocaleString()}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm bg-info text-white">
                            <div className="card-body">
                                <small className="text-uppercase opacity-75">Embroidery Total</small>
                                <h3 className="mb-0">₹{summary.embroidery.toLocaleString()}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-0 p-3">
                        <ul className="nav nav-pills nav-fill">
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'promotions' ? 'active' : ''}`} 
                                        onClick={() => setActiveTab('promotions')}>📢 Promotions</button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'karigar' ? 'active' : ''}`} 
                                        onClick={() => setActiveTab('karigar')}>🧵 Karigar Payments</button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'embroidery' ? 'active' : ''}`} 
                                        onClick={() => setActiveTab('embroidery')}>✨ Embroidery</button>
                            </li>
                        </ul>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4" style={{cursor: 'pointer'}} onClick={() => requestSort('date')}>
                                            Date {getSortIcon('date')}
                                        </th>
                                        <th style={{cursor: 'pointer'}} onClick={() => requestSort('title')}>
                                            {activeTab === 'promotions' ? 'Article / Campaign' : 'Worker Name'} {getSortIcon('title')}
                                        </th>
                                        <th style={{cursor: 'pointer'}} onClick={() => requestSort('source')}>
                                            Source {getSortIcon('source')}
                                        </th>
                                        <th style={{cursor: 'pointer'}} onClick={() => requestSort('amount')}>
                                            Amount {getSortIcon('amount')}
                                        </th>
                                        <th className="text-end pe-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="5" className="text-center py-5">
                                            <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                            Loading...
                                        </td></tr>
                                    ) : expenses.length > 0 ? (
                                        expenses.map((exp) => (
                                            <tr key={exp._id}>
                                                <td className="ps-4 text-muted small">
                                                    {exp.date ? new Date(exp.date).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="fw-bold">{exp.title || exp.payeeName}</td>
                                                <td>{exp.source}</td>
                                                <td className="text-danger fw-bold">₹{exp.amount}</td>
                                                <td className="text-end pe-4">
                                                    <span className="badge bg-light text-success border">Paid</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No expenses found for this category.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseDashboard;